import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Alert,
  Chip,
  Button,
  Snackbar
} from '@mui/material';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import TimerIcon from '@mui/icons-material/Timer';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CallEndIcon from '@mui/icons-material/CallEnd';
import PhoneIcon from '@mui/icons-material/Phone';
import VideocamIcon from '@mui/icons-material/Videocam';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useChat } from '../../contexts/ChatContext';
import api from '../../services/api';
import consultationService from '../../services/consultationService';
import {
  MessageList,
  MessageInput,
  ConnectionStatus,
  SessionTimer
} from '../chat';
import FloatingJitsiPiP from '../call/FloatingJitsiPiP';
import { useCall } from '../../contexts/CallContext';
import useCallLayout from '../../hooks/useCallLayout';
import useNetworkStatus from '../../hooks/useNetworkStatus';
import useOfflineMessageQueue from '../../hooks/useOfflineMessageQueue';

const AstrologerChat = () => {
  const { consultationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const layout = useCallLayout();
  const { activeCall } = useCall();

  // On-demand session state from navigation
  const locationState = location.state || {};
  const isInstant = locationState.isInstant || false;
  const callType = locationState.type; // 'chat' | 'call' | 'video'
  const ratePerMin = locationState.ratePerMin || 0;
  const chatRoomId = locationState.chatRoomId;
  const astrologerRate = ratePerMin * 0.70; // astrologer receives 70%

  const [astrologer, setAstrologer] = useState(null);
  const [consultation, setConsultation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [localMessages, setLocalMessages] = useState([]);

  // On-demand billing state
  const [elapsed, setElapsed] = useState(0);
  const [totalEarned, setTotalEarned] = useState(0);
  const [sessionEndingWarning, setSessionEndingWarning] = useState(false);
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);

  const { isOnline } = useNetworkStatus();

  const {
    socket,
    isConnected,
    messages,
    typingUsers,
    error: chatError,
    connect,
    disconnect,
    sendMessage
  } = useChat();

  // Offline queue integration
  const sendViaSocketOrRest = useCallback(async (msg) => {
    if (isConnected && socket) {
      sendMessage({
        consultation_id: msg.consultationId,
        content: msg.content,
        sender_type: 'astrologer',
        client_message_id: msg.clientMessageId,
      });
      return true;
    } else {
      const response = await consultationService.sendMessage(msg.consultationId, msg.content);
      return !!response?.data;
    }
  }, [isConnected, socket, sendMessage]);

  const {
    queueMessage,
    retryMessage,
    queuedMessages,
  } = useOfflineMessageQueue({
    userId: astrologer?.id?.toString() || '',
    sendMessage: sendViaSocketOrRest,
    getConsultationStatus: (id) => consultationService.getConsultationStatus(id),
    isOnline,
  });

  // Load astrologer info from localStorage
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      try {
        setAstrologer(JSON.parse(userData));
      } catch (e) {
        navigate('/login');
      }
    } else {
      navigate('/login');
    }
  }, [navigate]);

  // Load consultation details
  useEffect(() => {
    if (consultationId && astrologer) {
      loadConsultation();
    }
  }, [consultationId, astrologer]);

  // Connect to chat when consultation is loaded
  useEffect(() => {
    if (consultation && astrologer) {
      connect(consultation.id, null, astrologer.id);
    }
    return () => disconnect();
  }, [consultation, astrologer]);

  // Start elapsed timer for on-demand sessions
  useEffect(() => {
    if (!isInstant) return;
    startTimeRef.current = Date.now();
    timerRef.current = setInterval(() => {
      const secs = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setElapsed(secs);
      setTotalEarned((secs / 60) * astrologerRate);
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [isInstant, astrologerRate]);

  // Socket events for on-demand billing
  useEffect(() => {
    if (!socket || !isInstant) return;

    const onSessionEnding = (data) => {
      if (data.consultationId == consultationId) setSessionEndingWarning(true);
    };
    const onEnded = (data) => {
      if (data.consultationId == consultationId) {
        clearInterval(timerRef.current);
        navigate('/astrologer/dashboard', { state: { ended: true } });
      }
    };

    socket.on('session_ending_low_balance', onSessionEnding);
    socket.on('consultation_ended', onEnded);
    return () => {
      socket.off('session_ending_low_balance', onSessionEnding);
      socket.off('consultation_ended', onEnded);
    };
  }, [socket, isInstant, consultationId, navigate]);

  const loadConsultation = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await consultationService.getConsultation(consultationId);
      const data = response?.data?.data?.consultation || response?.data?.consultation || response?.data;
      setConsultation(data);
      await loadMessages();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load consultation');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const response = await consultationService.getMessages(consultationId);
      const msgs = response?.data?.data?.messages || response?.data?.messages || [];
      setLocalMessages(msgs);
    } catch (err) {
      console.error('Failed to load messages:', err);
    }
  };

  const handleSendMessage = async (content) => {
    if (!content.trim() || !consultationId || !astrologer) return;

    if (!isOnline) {
      // OFFLINE: queue + pending UI
      const clientMessageId = crypto.randomUUID();
      const entry = queueMessage(consultationId, content.trim(), clientMessageId);
      if (entry) {
        setLocalMessages(prev => [...prev, {
          id: entry.id,
          client_message_id: entry.clientMessageId,
          content: entry.content,
          consultation_id: consultationId,
          sender_id: astrologer.id,
          sender_type: 'astrologer',
          sent_at: entry.createdAt,
          message_type: 'text',
          delivery_status: null,
          is_queued: true,
          queue_status: 'pending',
        }]);
      }
    } else if (isConnected) {
      // ONLINE + SOCKET: normal path (unchanged behavior)
      sendMessage({
        consultation_id: consultationId,
        sender_id: astrologer.id,
        sender_type: 'astrologer',
        content: content.trim(),
        message_type: 'text',
      });
    } else {
      // ONLINE + NO SOCKET: REST fallback (unchanged behavior)
      try {
        const response = await consultationService.sendMessage(consultationId, content.trim());
        const newMessage = response?.data?.data?.message || response?.data?.message;
        if (newMessage) setLocalMessages(prev => [...prev, newMessage]);
      } catch (err) {
        console.error('Failed to send message via REST:', err);
      }
    }
  };

  const handleRetryMessage = useCallback((messageId) => {
    retryMessage(messageId);
    setLocalMessages(prev => prev.map(msg =>
      msg.id === messageId ? { ...msg, queue_status: 'pending' } : msg
    ));
  }, [retryMessage]);

  // Merge server messages + local messages + queued messages for display
  const allMessages = useMemo(() => {
    const combined = [...messages, ...localMessages];
    const existingIds = new Set(combined.map(m => m.client_message_id).filter(Boolean));
    const newQueued = queuedMessages
      .filter(q => q.status !== 'sent' && !existingIds.has(q.clientMessageId))
      .map(q => ({
        id: q.id,
        client_message_id: q.clientMessageId,
        content: q.content,
        consultation_id: consultationId,
        sender_id: astrologer?.id,
        sender_type: 'astrologer',
        sent_at: q.createdAt,
        is_queued: true,
        queue_status: q.status === 'sending' ? 'processing' : q.status,
      }));
    return [...combined, ...newQueued].sort((a, b) =>
      new Date(a.sent_at) - new Date(b.sent_at)
    );
  }, [messages, localMessages, queuedMessages, consultationId, astrologer?.id]);

  const handleEndSession = async () => {
    clearInterval(timerRef.current);
    try {
      await api.post(`/consultations/${consultationId}/end`);
    } catch (err) {
      console.error('End session error', err);
    }
    navigate('/astrologer/dashboard');
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  if (loading) {
    return <Box p={3}><Typography>Loading consultation...</Typography></Box>;
  }
  if (error) {
    return <Box p={3}><Alert severity="error">{error}</Alert></Box>;
  }

  const isCallType = callType === 'call' || callType === 'video';

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      {/* Header */}
      <AppBar position="static" elevation={1} sx={{ bgcolor: isInstant ? 'primary.dark' : 'secondary.main' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/astrologer/dashboard')}>
            <BackIcon />
          </IconButton>

          <Avatar sx={{ ml: 2, mr: 2, bgcolor: 'primary.main' }}>
            {consultation?.user?.name?.[0] || 'U'}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h6">
              {consultation?.user?.name || 'User'}
            </Typography>
            <Typography variant="caption">
              {isInstant ? 'On-demand consultation' : `Consultation #${consultationId}`}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            {isInstant ? (
              <>
                {callType === 'call' && <Chip icon={<PhoneIcon />} label="Voice Call" size="small" color="primary" />}
                {callType === 'video' && <Chip icon={<VideocamIcon />} label="Video Call" size="small" color="secondary" />}
                <Chip icon={<TimerIcon />} label={formatTime(elapsed)} size="small" />
                <Chip label={`₹${astrologerRate.toFixed(2)}/min`} size="small" color="warning" />
                <Chip
                  icon={<AccountBalanceWalletIcon />}
                  label={`₹${totalEarned.toFixed(0)} earned`}
                  size="small"
                  color="success"
                />
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<CallEndIcon />}
                  onClick={handleEndSession}
                >
                  End
                </Button>
              </>
            ) : (
              <>
                <ConnectionStatus isConnected={isConnected} error={chatError} />
                {consultation?.start_time && (
                  <SessionTimer startTime={consultation.start_time} duration={900} />
                )}
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      {chatError && <Alert severity="error" sx={{ m: 1 }}>{chatError}</Alert>}

      <Snackbar
        open={sessionEndingWarning}
        message="⚠️ User's balance is very low — session ending soon"
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {/* Floating Jitsi PiP — same design as user side.
          GUARD: Skip if GlobalCallPiP already has an active call — prevents
          duplicate JitsiMeetExternalAPI connections.  See #343. */}
      {isInstant && isCallType && !activeCall && (
        <FloatingJitsiPiP
          consultationId={consultationId}
          chatRoomId={chatRoomId || consultation?.chat_room_id}
          displayName={astrologer?.name || 'Astrologer'}
          videoMuted={callType === 'call'}
          callType={callType}
          onEnd={handleEndSession}
          onDashboardClick={() => navigate('/astrologer/dashboard')}
        >
          {/* Chat content as children for split-screen mode */}
          <>
            <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
              <MessageList
                messages={allMessages}
                currentUserId={astrologer?.id}
                currentUserType="astrologer"
                typingUsers={typingUsers}
              />
            </Box>
            <Box sx={{ p: 2 }}>
              <MessageInput
                onSendMessage={handleSendMessage}
                disabled={false}
                placeholder="Type your message..."
              />
            </Box>
          </>
        </FloatingJitsiPiP>
      )}

      {/* Chat area — visible in PiP mode and when no active call */}
      {(!isCallType || layout.mode === 'pip') && (
        <>
          <Box sx={{ flex: 1, overflow: 'hidden', minHeight: 0 }}>
            <MessageList
              messages={allMessages}
              currentUserId={astrologer?.id}
              currentUserType="astrologer"
              typingUsers={typingUsers}
            />
          </Box>
          <Box sx={{ p: 2 }}>
            {!isConnected && (
              <Alert severity="warning" sx={{ mb: 1 }}>
                Socket not connected. Messages sent via REST.
              </Alert>
            )}
            <MessageInput
              onSendMessage={handleSendMessage}
              disabled={false}
              placeholder="Type your message..."
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default AstrologerChat;
